/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit'
import { SIM_OPTIONS, CONSUMER_SIM_OPTIONS } from '@apps/utils/constants'
import { formatSimField } from '@apps/utils/formHelpers'

export const SIM_SELECTION_STATE = {
  SIM_ERROR: 'simError',
  SIM: 'simNumber'
}

// A SIM the customer already owns is never sold to them, so the "already have
// a SIM" selection must never carry a cost. The reset paths that serve this
// option hand over the physical-SIM price (they double as the buy-a-SIM
// paths), so the cost is normalized here — one place every writer of
// selectSimType goes through — otherwise the order summary silently adds the
// pSIM price to the subtotal, tax and amount owing.
const isHaveSimSelection = (simType) => (
  `${simType || ''}`.toLowerCase() === SIM_OPTIONS.H_SIM.toLowerCase()
)

const normalizeSimCost = (selectSimType) => {
  if (selectSimType && isHaveSimSelection(selectSimType.simType)) {
    selectSimType.cost = 0
  }
}

const SimSelectionComplete = (state) => {
  const { selectSimType, simNumber, simError } = state
  const { currentPageModule } = selectSimType

  if (
    currentPageModule === SIM_OPTIONS.P_SIM ||
    currentPageModule === CONSUMER_SIM_OPTIONS.HAVE_SIM
  ) {
    return !!simNumber && !simError
  }
  return true
}

export const simSelectionPage = createSlice({
  name: 'simSelectionPage',
  initialState: {
    selectSimType: {
      currentPageModule: '',
      // Empty until the user (or a restored quote) picks a SIM type. The blank
      // sentinel lets the SIM page default a fresh entry to eSIM, while staying
      // non-eSIM everywhere else (payment summary, process loader copy).
      simType: '',
      cost: 0
    },
    simNumber: '',
    esimNumber: '',
    simError: '',
    imei: '',
    imeiChecked: false,
    imeiCompatible: false,
    eid: '',
    isComplete: false
  },
  reducers: {
    recordSimSelectionInfo: (state, action) => {
      if (!action.payload) return
      const { simType, simNumber, eSimPrice } = action.payload
      if (simType) {
        const simTypeValue =
          simType.toLowerCase() === CONSUMER_SIM_OPTIONS.HAVE_SIM.toLowerCase()
            ? SIM_OPTIONS.H_SIM
            : SIM_OPTIONS.E_SIM

        const update = {
          selectSimType: {
            simType: simTypeValue,
            cost: simTypeValue === SIM_OPTIONS.H_SIM ? 0 : eSimPrice
          }
        }

        if (simTypeValue === SIM_OPTIONS.H_SIM) {
          const { formattedSim } = formatSimField(simNumber)
          update.simNumber = formattedSim
        }

        state = Object.assign(state, update)
      }
    },
    updateSimSelectionState: (state, action) => {
      const newState = Object.assign(state, action.payload)
      normalizeSimCost(newState.selectSimType)
      newState.isComplete = SimSelectionComplete(newState)
      state = { ...newState }
    },

    setSimType: (state, action) => {
      state.selectSimType.simType = action.payload.simType
      state.selectSimType.cost = action.payload.cost
      normalizeSimCost(state.selectSimType)
      state.isComplete = SimSelectionComplete(state)
    },

    setSimNumber: (state, action) => {
      state.simNumber = action.payload
      state.isComplete = SimSelectionComplete(state)
    },

    setESimNumber: (state, action) => {
      state.esimNumber = action.payload
    },

    reset: (state, action) => {
      state.selectSimType.simType = action.payload.simType
      state.isComplete = SimSelectionComplete(state)
    },

    setPageModule: (state, action) => {
      state.selectSimType.currentPageModule = action.payload
      state.isComplete = SimSelectionComplete(state)
    }
  }
})

// this is for dispatch
export const {
  recordSimSelectionInfo,
  setSimType,
  updateSimSelectionState,
  setSimNumber,
  setESimNumber,
  reset,
  setPageModule
} = simSelectionPage.actions

// this is for configureStore
export default simSelectionPage.reducer
